import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorMode,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  Divider,
  Link,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, CloseIcon } from "@chakra-ui/icons";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface CountryAccess {
  country: string;
  status: "granted" | "negotiating" | "blocked";
  detail: string;
  dateConfirmed?: string;
}

const countryAccess: CountryAccess[] = [
  { country: "China", status: "granted", detail: "Full safe passage. Major Iranian oil buyer.", dateConfirmed: "Early March 2026" },
  { country: "India", status: "granted", detail: "Full safe passage. Direct talks with Tehran.", dateConfirmed: "Early March 2026" },
  { country: "Pakistan", status: "granted", detail: "Full safe passage. Neighbouring state.", dateConfirmed: "Early March 2026" },
  { country: "Iraq", status: "granted", detail: "Full safe passage. Regional ally.", dateConfirmed: "Early March 2026" },
  { country: "Malaysia", status: "granted", detail: "Direct talks held with Tehran.", dateConfirmed: "Mid March 2026" },
  { country: "Japan", status: "negotiating", detail: "FM Araghchi confirmed readiness to allow Japanese ships. Talks ongoing with FM Motegi.", dateConfirmed: "21 March 2026" },
  { country: "Australia", status: "blocked", detail: "Five Eyes ally of US. Contributed F-35 parts to Israel. Pine Gap intelligence sharing. Likely classified as 'enemy' by Iran.", dateConfirmed: undefined },
  { country: "United States", status: "blocked", detail: "Led strikes on Iran 28 Feb 2026. Primary 'enemy' target.", dateConfirmed: undefined },
  { country: "Israel", status: "blocked", detail: "Joint strikes on Iran. Killed Supreme Leader Khamenei.", dateConfirmed: undefined },
  { country: "United Kingdom", status: "blocked", detail: "Western ally. Part of 22-nation coalition seeking safe passage.", dateConfirmed: undefined },
  { country: "France", status: "blocked", detail: "Part of 22-nation coalition seeking safe passage.", dateConfirmed: undefined },
  { country: "Germany", status: "blocked", detail: "Part of 22-nation coalition seeking safe passage.", dateConfirmed: undefined },
];

const statusIcon = (status: string) => {
  switch (status) {
    case "granted":
      return CheckCircleIcon;
    case "negotiating":
      return WarningIcon;
    case "blocked":
      return CloseIcon;
    default:
      return WarningIcon;
  }
};

const statusColor = (status: string) => {
  switch (status) {
    case "granted":
      return "green";
    case "negotiating":
      return "yellow";
    case "blocked":
      return "red";
    default:
      return "gray";
  }
};

const IranHormuzUpdate: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Iran Hormuz Update</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Badge colorScheme="red" alignSelf="start" fontSize="sm" px={3} py={1}>
            BREAKING -- 21 March 2026
          </Badge>

          <Heading size="lg" color="#dc2626">
            Iran's Selective Hormuz Blockade: Japan Gets Access, Australia Still Blocked
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            Iran's Foreign Minister Araghchi confirmed Japan will be allowed to transit the Strait of Hormuz.
            Iran is running a selective blockade -- not a total closure -- blocking "enemies" while allowing others through.
            What does this mean for Australia?
          </Text>

          {/* Key Stats */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Blockade Type</StatLabel>
              <StatNumber color="orange.400" fontSize="xl">Selective</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">not total closure</StatHelpText>
            </Stat>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Countries Granted</StatLabel>
              <StatNumber color="green.400" fontSize="2xl">5+</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">China, India, Pakistan, Iraq, Malaysia</StatHelpText>
            </Stat>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Japan Status</StatLabel>
              <StatNumber color="yellow.400" fontSize="xl">Talks Active</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">FM confirmed readiness</StatHelpText>
            </Stat>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Australia Status</StatLabel>
              <StatNumber color="#dc2626" fontSize="xl">Blocked</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">classified as US ally</StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* What Happened */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              What Happened
            </Heading>
            <VStack align="start" spacing={3}>
              <Text fontSize="sm" color={textPrimary}>
                <strong>28 February 2026:</strong> US and Israel launched joint military strikes on Iran, killing Supreme Leader Khamenei.
                Iran retaliated with missile and drone attacks on US bases, Israeli territory, and Gulf states.
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>5 March 2026:</strong> Iran's IRGC announced a selective blockade -- the Strait of Hormuz would be
                "closed only to ships belonging to our enemies, countries that attack us. For other countries, ships can pass through."
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>Mid March 2026:</strong> China, India, Pakistan, Iraq, and Malaysia confirmed as having safe passage. Iran
                continued exporting its own oil through the strait.
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>19 March 2026:</strong> Iranian drone attacks on regional refineries further impacted global supply capacity.
                22 nations (including UK, France, Japan) formed a coalition seeking safe passage.
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>20 March 2026:</strong> Trump administration eased sanctions on certain Iranian oil stockpiles, allowing
                Iranian oil to temporarily reach allies like Japan -- a counter-move to Iran's strategy.
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>21 March 2026:</strong> Iran's Foreign Minister Araghchi told Kyodo News that Iran is ready to allow
                Japanese-linked vessels to transit Hormuz. Discussions already underway with FM Motegi.
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>21-22 March 2026:</strong> Trump threatened to "obliterate" Iran's power plants if the Strait of Hormuz
                is not fully reopened within 48 hours. Brent crude peaked at US$126/barrel -- fastest conflict-driven surge in history.
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                <strong>22 March 2026:</strong> 22 nations (including UAE, UK, France, Germany, Japan, Bahrain) issued joint statement
                condemning Iran's closure and attacks on commercial vessels and civilian infrastructure including oil/gas facilities.
              </Text>
            </VStack>
          </Box>

          {/* Country Access Table */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Hormuz Access by Country
            </Heading>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th color={textSecondary}>Country</Th>
                    <Th color={textSecondary}>Status</Th>
                    <Th color={textSecondary}>Detail</Th>
                    <Th color={textSecondary}>Confirmed</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {countryAccess.map((c) => (
                    <Tr key={c.country}>
                      <Td color={textPrimary} fontWeight="bold">{c.country}</Td>
                      <Td>
                        <Badge colorScheme={statusColor(c.status)} fontSize="2xs">
                          {c.status}
                        </Badge>
                      </Td>
                      <Td color={textPrimary} fontSize="xs">{c.detail}</Td>
                      <Td color={textSecondary} fontSize="xs">{c.dateConfirmed || "--"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Australia Impact */}
          <Alert status="error" borderRadius="lg" bg="rgba(220,38,38,0.1)" border="1px solid" borderColor="red.600">
            <AlertIcon color="#dc2626" />
            <Box>
              <Text fontSize="sm" color={textPrimary} fontWeight="bold">
                Australia is already being hit
              </Text>
              <Text fontSize="sm" color={textPrimary}>
                107+ fuel stations across NSW have run out of diesel. Panic buying doubled normal fuel sales within 10 days.
                Diesel hit $3/litre. Government released 762 million litres from reserves and lowered fuel quality standards
                for 60 days (allowing higher-sulfur fuel).
              </Text>
            </Box>
          </Alert>

          {/* Does Japan Deal Help Australia? */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Does the Japan Deal Help Australia?
            </Heading>
            <Text fontSize="sm" color={textPrimary} mb={4}>
              This is the critical question. Australia imports ~90% of its refined diesel, mostly from
              Singapore, South Korea, Japan, Malaysia, and Taiwan. Here's the chain:
            </Text>

            <Box p={4} borderRadius="md" border="1px solid" borderColor="green.600" bg="rgba(5,150,105,0.05)" mb={4}>
              <Text fontSize="sm" color="green.400" fontWeight="bold" mb={2}>
                INDIRECT BENEFIT -- Yes, partially
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={CheckCircleIcon} color="green.400" />
                  If Japan can import crude oil through Hormuz, Japanese and Korean refineries can keep processing
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={CheckCircleIcon} color="green.400" />
                  Refined diesel shipped from Japan/Korea to Australia does NOT transit Hormuz -- it goes via open Pacific waters
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={CheckCircleIcon} color="green.400" />
                  More crude reaching Asian refineries = more refined product available globally = lower prices
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={CheckCircleIcon} color="green.400" />
                  China and India already have access, so Singapore refineries (fed by their imports) may also benefit
                </ListItem>
              </List>
            </Box>

            <Box p={4} borderRadius="md" border="1px solid" borderColor="red.600" bg="rgba(220,38,38,0.05)">
              <Text fontSize="sm" color="red.400" fontWeight="bold" mb={2}>
                BUT -- Major caveats
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={WarningIcon} color="red.400" />
                  Australia's OWN ships are still blocked -- any Australian-flagged or Australian-bound tankers transiting Hormuz are at risk
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={WarningIcon} color="red.400" />
                  The 35-40% of Australia's oil that comes directly from the Middle East is still cut off
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={WarningIcon} color="red.400" />
                  Refinery capacity is limited -- even if crude gets through, refineries are already at max capacity due to global demand surge
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={WarningIcon} color="red.400" />
                  Japan will prioritise its own 127 million people before exporting refined product to Australia
                </ListItem>
                <ListItem fontSize="sm" color={textPrimary}>
                  <ListIcon as={WarningIcon} color="red.400" />
                  Iran's "selective blockade" could change at any time -- deals can be revoked
                </ListItem>
              </List>
            </Box>
          </Box>

          {/* Government Response */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Australia's Government Response (So Far)
            </Heading>
            <List spacing={3}>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="orange.400" />
                <strong>762 million litres released</strong> from reserves via Minimum Stockholding Obligation framework
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="orange.400" />
                <strong>Fuel quality standards lowered</strong> for 60 days -- allowing higher-sulfur fuel to add ~100M litres/month
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="orange.400" />
                <strong>National Cabinet response</strong> -- crisis meetings underway
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="orange.400" />
                <strong>No direct talks with Iran</strong> -- Australia has not (publicly) sought its own safe passage deal
              </ListItem>
            </List>
          </Box>

          {/* What Should Australia Do? */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Key Questions for Further Research
            </Heading>
            <List spacing={2}>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="#dc2626" />
                Should Australia seek its own safe passage deal with Iran, or does the Five Eyes alliance prevent this?
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="#dc2626" />
                Can Australia ramp up imports from the US Pacific route fast enough to fill the gap?
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="#dc2626" />
                If Japan gets access, will South Korea and Taiwan follow? (They supply most of AU's refined product)
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="#dc2626" />
                How many more days of reserves does Australia actually have after the 762M litre release?
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="#dc2626" />
                What happens when the 60-day fuel quality waiver expires?
              </ListItem>
              <ListItem fontSize="sm" color={textPrimary}>
                <ListIcon as={WarningIcon} color="#dc2626" />
                Is the 22-nation coalition viable, or will Iran pick off countries one by one with bilateral deals?
              </ListItem>
            </List>
          </Box>

          {/* Sources */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="sm" color={textSecondary} mb={3}>
              Sources
            </Heading>
            <VStack align="start" spacing={1}>
              <Link href="https://www.aljazeera.com/news/2026/3/21/iran-says-it-will-allow-japanese-ships-to-transit-the-strait-of-hormuz" isExternal fontSize="xs" color="blue.400">
                Al Jazeera -- Iran says it will allow Japanese ships to transit the Strait of Hormuz (21 Mar 2026)
              </Link>
              <Link href="https://www.bloomberg.com/news/articles/2026-03-21/iran-says-ready-to-let-japan-vessels-use-hormuz-kyodo-reports" isExternal fontSize="xs" color="blue.400">
                Bloomberg -- Iran Says Ready to Let Japan Vessels Use Hormuz (21 Mar 2026)
              </Link>
              <Link href="https://www.aljazeera.com/economy/2026/3/16/strait-of-hormuz-which-countriess-ships-has-iran-allowed-safe-passage-to" isExternal fontSize="xs" color="blue.400">
                Al Jazeera -- Which countries' ships has Iran allowed safe passage to? (16 Mar 2026)
              </Link>
              <Link href="https://www.aspistrategist.org.au/hormuz-closure-brings-australias-layered-fuel-vulnerability-to-the-fore/" isExternal fontSize="xs" color="blue.400">
                ASPI Strategist -- Hormuz closure brings Australia's fuel vulnerability to the fore
              </Link>
              <Link href="https://www.sbs.com.au/news/article/australia-fuel-shortage-2026/zl0grg7ey" isExternal fontSize="xs" color="blue.400">
                SBS -- Amid rationing and shortage fears, Australia's fuel supply in charts
              </Link>
              <Link href="https://www.cnn.com/2026/03/20/politics/iran-oil-sanctions-lifting" isExternal fontSize="xs" color="blue.400">
                CNN -- Trump administration easing sanctions on Iranian oil stockpiles (20 Mar 2026)
              </Link>
              <Link href="https://en.wikipedia.org/wiki/2026_Strait_of_Hormuz_crisis" isExternal fontSize="xs" color="blue.400">
                Wikipedia -- 2026 Strait of Hormuz crisis
              </Link>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default IranHormuzUpdate;
